/** The development site's hosts. `preview.dev.240.org` is the name before the Labs rename and still routes. */
const DEVELOPMENT_HOSTS = ['labs.dev.240.org', 'preview.dev.240.org'];

export function previewDestinations(hostname: string) {
  const development = DEVELOPMENT_HOSTS.includes(hostname);
  const source = development ? 'marketing-dev' : 'marketing';
  return {
    development,
    jobs: jobsDestination(hostname),
    studyPreview: development ? 'https://study.dev.240tutoring.com/preview' : 'https://study.240tutoring.com/preview',
    oneStepAhead: development
      ? 'https://240-preview-one-step-ahead-preview.240-tutoring.workers.dev?from=marketing-dev'
      : 'https://ahead.labs.240.org?from=marketing',
    objectives: development
      ? 'https://homeroom-objectives-preview.240-tutoring.workers.dev?from=marketing-dev'
      : 'https://objectives.labs.240.org?from=marketing',
    rubric: development
      ? 'https://240-preview-rubric-preview.240-tutoring.workers.dev?from=marketing-dev'
      : 'https://rubric.labs.240.org?from=marketing',
    // 240 Sites has no development deployment on its own hostname yet, so development
    // points at its preview Worker — the same shape Objectives uses above.
    sites: development
      ? 'https://240-sites-dashboard-preview.240-tutoring.workers.dev?from=marketing-dev'
      : 'https://sites.labs.240.org?from=marketing',
    // 240 Collab is production-only: there is no staging Worker, because one would need a
    // second D1, a second R2 bucket and the `pk_test_` Clerk instance, none of which are
    // provisioned. The development site therefore links to the one Collab that exists, and
    // says so with `from=marketing-dev` — a workers.dev URL here would be a dead link.
    writingFeedback: `https://writing.labs.240.org?from=${source}`,
    // AI Detector is production-only too.
    aiDetector: `https://ai-detector.labs.240.org?from=${source}`,
    collab: `https://collab.labs.240.org?from=${source}`,
    // B-Side is production-only, like Collab: no development Worker exists to link to.
    bSide: `https://bside.labs.240.org?from=${source}`,
    // The Planner is production-only too.
    planner: `https://planner.labs.240.org?from=${source}`,
    // Profile is production-only too.
    profile: `https://profile.labs.240.org?from=${source}`,
  };
}

/** No guessed hosted URL: an unconfigured environment cannot advertise an open tool. */
export function jobsDestination(hostname: string, config = {
  production: import.meta.env.VITE_JOBS_URL as string | undefined,
  preview: import.meta.env.VITE_JOBS_PREVIEW_URL as string | undefined,
}): string | undefined {
  const preview = DEVELOPMENT_HOSTS.includes(hostname) || ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
  const value = preview ? config.preview : config.production;
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password) return undefined;
    url.searchParams.set('from', preview ? 'marketing-dev' : 'marketing');
    return url.toString();
  } catch { return undefined; }
}
